import React, { useEffect, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";

const API_URL =
  process.env.REACT_APP_MESSAGE_URL || "http://localhost:3000/api/message";
const API_KEY = process.env.REACT_APP_API_KEY;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const DESIGN_W = 1080;
const DESIGN_H = 1920;

const PRESET_MESSAGES = [
  "Chopped seats at lunch",
  "Fixed my cravings today",
  "Food arrived still hot",
  "Got the last piece of pizza",
  "Made it out of groupchat",
  "Savour meal moments",
  "Everyone agreed on dinner",
  "Fixed this week’s menu",
  "No dinner complaints today",
  "Made it through Monday",
  "Lunch meeting got cancelled",
];

const Messages = () => {
  const stageRef = useRef(null);
  const [step, setStep] = useState(1);
  const [pdpa1, setPdpa1] = useState(false);
  const [pdpa2, setPdpa2] = useState(false);
  const [text, setText] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — should stay empty
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fit = () => {
      if (!stageRef.current) return;
      const s = Math.min(
        window.innerWidth / DESIGN_W,
        window.innerHeight / DESIGN_H
      );
      stageRef.current.style.transform = `scale(${s})`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  const reset = () => {
    setStep(1);
    setPdpa1(false);
    setPdpa2(false);
    setText("");
    setError(null);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
        body: JSON.stringify({ text, website, turnstileToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || "Failed to submit");
      } else {
        setStep(4);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const bothChecked = pdpa1 && pdpa2;
  const hasText = text.trim().length > 0;

  return (
    <div className="fixed inset-0 overflow-hidden flex items-center justify-center bg-white">
      <div
        ref={stageRef}
        className="font-betterwith"
        style={{
          position: "relative",
          width: DESIGN_W,
          height: DESIGN_H,
          flexShrink: 0,
          transformOrigin: "center center",
          transform: "scale(1)",
          backgroundImage: `url(/img/${step}/${step}@2x.png)`,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
        }}
      >
        {step === 1 && (
          <>
            {/* Red overlay covering baked-in PDPA checkboxes + text in the bg */}
            <div
              style={{
                position: "absolute",
                top: 1260,
                left: 80,
                width: 920,
                height: 340,
                background: "#f50008",
              }}
            />

            <button
              onClick={() => setStep(2)}
              style={{
                position: "absolute",
                top: 1680,
                left: 290,
                width: 500,
              }}
            >
              <img
                src="/img/1/start@2x.png"
                alt="Start"
                style={{ width: "100%", display: "block" }}
              />
            </button>
          </>
        )}

        {step === 2 && (
          <>
            {/* Honeypot — invisible to real users, attracts bots. Must stay empty. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              style={{
                position: "absolute",
                left: "-9999px",
                top: 0,
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: "none",
              }}
            />

            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={45}
              placeholder="Enter Here"
              style={{
                position: "absolute",
                top: 1170,
                left: 100,
                width: 880,
                height: 130,
                fontFamily: "BetterWith, serif",
                fontSize: 48,
                textAlign: "center",
                background: "#fff",
                color: "#000",
                border: "4px solid #000",
                borderRadius: 16,
                outline: "none",
              }}
            />

            <select
              value={PRESET_MESSAGES.includes(text) ? text : ""}
              onChange={(e) => setText(e.target.value)}
              style={{
                position: "absolute",
                top: 1370,
                left: 100,
                width: 880,
                height: 130,
                fontFamily: "BetterWith, serif",
                fontSize: 48,
                textAlign: "center",
                background: "#000",
                color: "#fff",
                border: "none",
                borderRadius: 16,
                outline: "none",
                appearance: "none",
                padding: "0 40px",
              }}
            >
              <option value="">Small Win List</option>
              {PRESET_MESSAGES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>

            {TURNSTILE_SITE_KEY && (
              <div
                style={{
                  position: "absolute",
                  top: 1545,
                  left: 390,
                  width: 300,
                  transform: "scale(1.3)",
                  transformOrigin: "top center",
                }}
              >
                <Turnstile
                  siteKey={TURNSTILE_SITE_KEY}
                  options={{ theme: "light", size: "normal" }}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => setTurnstileToken("")}
                  onExpire={() => setTurnstileToken("")}
                />
              </div>
            )}

            <button
              onClick={() => hasText && (!TURNSTILE_SITE_KEY || turnstileToken) && setStep(3)}
              disabled={!hasText || (TURNSTILE_SITE_KEY && !turnstileToken)}
              style={{
                position: "absolute",
                top: 1680,
                left: 290,
                width: 500,
                opacity: hasText && (!TURNSTILE_SITE_KEY || turnstileToken) ? 1 : 0.4,
                transition: "opacity 0.2s",
              }}
            >
              <img
                src="/img/2/next@2x.png"
                alt="Next"
                style={{ width: "100%", display: "block" }}
              />
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <div
              style={{
                position: "absolute",
                top: 1050,
                left: 200,
                width: 680,
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "#000",
                fontFamily: "BetterWith, serif",
                fontSize: 64,
                fontWeight: 700,
                lineHeight: 1.1,
                padding: "0 20px",
              }}
            >
              {text}
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={submitting}
              style={{
                position: "absolute",
                top: 1680,
                left: 140,
                width: 360,
              }}
            >
              <img
                src="/img/3/back@2x.png"
                alt="Back"
                style={{ width: "100%", display: "block" }}
              />
            </button>

            <button
              onClick={submit}
              disabled={submitting}
              style={{
                position: "absolute",
                top: 1680,
                left: 580,
                width: 360,
                opacity: submitting ? 0.4 : 1,
              }}
            >
              <img
                src="/img/3/submit@2x.png"
                alt="Submit"
                style={{ width: "100%", display: "block" }}
              />
            </button>

            {error && (
              <div
                style={{
                  position: "absolute",
                  bottom: 30,
                  left: 0,
                  width: "100%",
                  textAlign: "center",
                  color: "#fff",
                  fontSize: 28,
                }}
              >
                {error}
              </div>
            )}
          </>
        )}

        {step === 4 && (
          <button
            onClick={reset}
            style={{
              position: "absolute",
              top: 1680,
              left: 290,
              width: 500,
            }}
          >
            <img
              src="/img/4/home@2x.png"
              alt="Home"
              style={{ width: "100%", display: "block" }}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default Messages;
